/**
 * The applicability matrix, as executable data.
 *
 * Source: `design/Oprappo P0 States.dc.html`, the seven-column grid at lines 679–787.
 * A cell drawn `background:#fff` with the word `হ্যাঁ` is `true`; a cell drawn
 * `background:#F9F7F2` and empty is `false`. The design's own caption for the tinted
 * cell: "A tinted empty cell means the state cannot occur on that surface."
 *
 * WHY THIS IS DATA AND NOT A COMMENT
 *
 * `false` here is not "not built yet". It is "structurally impossible", and
 * `resolveScreenState` enforces it: asking the AI surface for `payment_failed`
 * returns null, not a payment screen. A matrix that only lived in a document would be
 * satisfied by a screen that renders a state the design says cannot happen there, and
 * nothing would notice.
 *
 * THE COUNT — READ THIS BEFORE CHANGING IT
 *
 * Counting the `হ্যাঁ` cells in the drawn grid gives 45. Three places say otherwise:
 *
 *   design, caption under the grid   "49 state implementations … not 430"
 *   docs/10 § WP-K                   "49 implementations"
 *   HANDOFF § definition of done     "49 implementations, not 451"
 *
 * 45 is what the grid contains; `matrix.test` counts it from this table and would fail
 * if an edit moved it. The four-cell gap is reported, not closed — closing it would
 * mean adding four cells the design does not draw. Two further arithmetic
 * disagreements sit in the same caption and are reported with it: the grid's intro
 * says 451 combinations (11 × 41) and its caption says 430, and the caption says
 * "Payment failure reaches five surfaces" where the row has four.
 *
 * ── ONE CELL THIS FILE NO LONGER READS OFF THE GRID ──────────────────────────
 *
 * অনুমতি নেই × অ্যাকাউন্ট is `হ্যাঁ` in the drawn grid and `false` here, since W1-04. The
 * drawn reason for the cell was founder mode: § ১০ is drawn on its dark ground, and
 * INTEGRATION.md § 3 filed the founder group under the account column. Founder mode now
 * lives on its own host and its screens use the internal column (founder decision
 * 2026-09-18), so the account column is the customer's account and nothing else — and a
 * customer has one role. What actually reached this cell in production was a signed-out
 * customer's 401 on their own account page, drawn as a finance officer's cap-table refusal.
 * A missing session is the sign-in door (`auth/SignInPrompt.tsx`), never a refusal.
 *
 * So the count read off the grid is 45 and the count enforced is 44; both are asserted,
 * and the gate row that captured the cell (S10) is recorded as DEVIATED in
 * `docs/deviations/W1-04.md` rather than the design file being edited.
 */
import type { StateKey } from './keys.js';
import { STATE_KEYS } from './keys.js';

/**
 * The seven columns, left to right, with the drawn Bangla header.
 *
 * `internal` is `অভ্যন্তরীণ` — the fulfilment console and the order queue (G1, G2),
 * which are WP-H's, not this package's. See INTEGRATION.md.
 */
export type Surface =
  | 'onboarding'
  | 'discovery'
  | 'ai'
  | 'transaction'
  | 'activity'
  | 'account'
  | 'internal';

export const SURFACES: readonly Surface[] = [
  'onboarding',
  'discovery',
  'ai',
  'transaction',
  'activity',
  'account',
  'internal',
];

export const SURFACE_LABEL_BN: Record<Surface, string> = {
  onboarding: 'অনবোর্ডিং',
  discovery: 'আবিষ্কার',
  ai: 'এআই',
  transaction: 'লেনদেন',
  activity: 'অ্যাক্টিভিটি',
  account: 'অ্যাকাউন্ট',
  internal: 'অভ্যন্তরীণ',
};

/**
 * Which screens each column covers, from
 * `design/Oprappo Architecture.dc.html § 15 screen inventory` (the P0 table). The
 * matrix is drawn per surface, not per screen, so this is what turns a column into a
 * set of routes.
 *
 * H1, H2 and H3 are post-audit additions and appear in no column of that inventory.
 * They are placed on `transaction` here — H1 is a provider's verification detail
 * reached from an offering, H2 is a refund term reached from a receipt, H3 is a
 * prepared basket that fills a cart. Reported as an ambiguity, not settled by the
 * design.
 */
export const SURFACE_SCREENS: Record<Surface, readonly string[]> = {
  onboarding: ['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7'],
  discovery: ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7'],
  ai: ['C1', 'C2', 'C3', 'C4', 'C5'],
  transaction: ['D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'H1', 'H2', 'H3'],
  activity: ['E1', 'E2', 'E3', 'E4'],
  account: ['F1', 'F2', 'F3', 'F4', 'F5', 'F6'],
  internal: ['G1', 'G2'],
};

/**
 * The grid itself. Row order is `STATE_KEYS`; column order is `SURFACES`.
 * Written as booleans in the drawn order so a reader can lay this against the design
 * file and check it cell by cell.
 */
const GRID: Record<StateKey, readonly boolean[]> = {
  //                     onboard discover  ai   transact activity account internal
  new_user: /*         */ [false, true, true, false, true, true, true],
  loading: /*          */ [true, true, true, true, true, true, true],
  offline: /*          */ [true, true, true, true, true, true, true],
  payment_failed: /*   */ [false, false, false, true, true, true, true],
  provider_unavailable: [false, true, true, true, false, false, true],
  verification_pending: [true, false, false, true, false, true, true],
  sync_error: /*       */ [false, true, false, false, true, false, true],
  // account: `হ্যাঁ` in the drawn grid, `false` since W1-04 — see "ONE CELL" above.
  permission_denied: /**/ [false, false, false, false, false, false, true],
  service_paused: /*   */ [false, true, true, true, false, false, true],
  domain_not_live: /*  */ [false, true, true, false, false, false, true],
  approval_pending: /* */ [false, false, false, false, true, false, true],
};

/** Does the design say this state can occur on this surface? */
export function applies(state: StateKey, surface: Surface): boolean {
  const row = GRID[state];
  const index = SURFACES.indexOf(surface);
  return index >= 0 && row[index] === true;
}

/** Every state a surface must implement, in matrix row order. */
export function statesFor(surface: Surface): StateKey[] {
  return STATE_KEYS.filter((s) => applies(s, surface));
}

/** Every surface a state must be implemented on, in matrix column order. */
export function surfacesFor(state: StateKey): Surface[] {
  return SURFACES.filter((s) => applies(state, s));
}

/**
 * The number of cells this table ENFORCES. 44: the 45 `হ্যাঁ` cells of the drawn grid,
 * less অনুমতি নেই × অ্যাকাউন্ট. Not a target to be met — a measurement to be reported.
 */
export function implementationCount(): number {
  return STATE_KEYS.reduce((n, s) => n + surfacesFor(s).length, 0);
}

/** The count of `হ্যাঁ` cells in the drawn grid itself, before the W1-04 departure. */
export const DRAWN_IMPLEMENTATION_COUNT = 45;

/**
 * The cells of the drawn grid this table deliberately does not enforce, each with the
 * decision behind it. `implementationCount() + DEPARTURES.length` is the drawn count.
 */
export const DEPARTURES: ReadonlyArray<{ state: StateKey; surface: Surface; why: string }> = [
  {
    state: 'permission_denied',
    surface: 'account',
    why:
      'a customer has one role; a signed-out 401 is the sign-in door, not a refusal ' +
      '(founder decision 2026-09-18: founder mode on its own host, internal column)',
  },
];

/** What the three documents claim, kept next to what was counted. */
export const DOCUMENTED_IMPLEMENTATION_COUNT = 49;

/** 11 states × 7 surfaces — the size of the grid, most of which is impossible. */
export function cellCount(): number {
  return STATE_KEYS.length * SURFACES.length;
}
